import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { useEffect, useState } from 'react';
import {
  Bold, Italic, Underline, List, ListOrdered, Link as LinkIcon,
  Heading2, Undo2, Redo2, Strikethrough, Quote, Unlink,
} from 'lucide-react';

const btn =
  'p-1.5 rounded hover:bg-gray-200 text-gray-700 disabled:opacity-40 disabled:cursor-not-allowed';
const activeBtn = 'bg-[#0a3d8f] text-white hover:bg-[#082e6b]';

const ToolbarBtn = ({ onClick, active, disabled, title, testid, children }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    data-testid={testid}
    className={`${btn} ${active ? activeBtn : ''}`}
  >
    {children}
  </button>
);

/**
 * Rich text editor for admin CMS.
 * Emits HTML string via onChange. Empty content emits ''.
 */
const RichTextField = ({ value, onChange, testid = 'richtext' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { class: 'text-[#0a3d8f] underline hover:text-[#082e6b]', rel: 'noopener noreferrer', target: '_blank' },
      }),
    ],
    content: value || '',
    editorProps: {
      attributes: {
        class:
          'prose prose-sm max-w-none min-h-[140px] px-3 py-2 focus:outline-none text-gray-800',
        'data-testid': `${testid}-editor`,
      },
    },
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      // Treat empty <p></p> as empty string
      onChange(html === '<p></p>' ? '' : html);
    },
  });

  // Sync external value changes (e.g. modal opening on a different record)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    const incoming = value || '';
    if (incoming !== current && incoming !== (current === '<p></p>' ? '' : current)) {
      editor.commands.setContent(incoming || '', false);
    }
  }, [value, editor]);

  const [showLink, setShowLink] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  if (!editor) return null;

  const openLinkPrompt = () => {
    const prev = editor.getAttributes('link')?.href || '';
    setLinkUrl(prev);
    setShowLink(true);
  };

  const applyLink = () => {
    const url = (linkUrl || '').trim();
    if (!url) {
      editor.chain().focus().unsetLink().run();
    } else {
      const normalized = /^https?:\/\//i.test(url) || url.startsWith('mailto:') || url.startsWith('/')
        ? url
        : `https://${url}`;
      editor.chain().focus().extendMarkRange('link').setLink({ href: normalized }).run();
    }
    setShowLink(false);
    setLinkUrl('');
  };

  return (
    <div className="border border-gray-200 rounded-md bg-white" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b bg-gray-50 rounded-t-md">
        <ToolbarBtn
          testid={`${testid}-bold`}
          title="Bold (Ctrl+B)"
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
        >
          <Bold size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          testid={`${testid}-italic`}
          title="Italic (Ctrl+I)"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
        >
          <Italic size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          testid={`${testid}-strike`}
          title="Strikethrough"
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
        >
          <Strikethrough size={15} />
        </ToolbarBtn>

        <span className="mx-1 w-px h-5 bg-gray-300" />

        <ToolbarBtn
          testid={`${testid}-h2`}
          title="Heading"
          onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          active={editor.isActive('heading', { level: 2 })}
        >
          <Heading2 size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          testid={`${testid}-quote`}
          title="Blockquote"
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
        >
          <Quote size={15} />
        </ToolbarBtn>

        <span className="mx-1 w-px h-5 bg-gray-300" />

        <ToolbarBtn
          testid={`${testid}-ul`}
          title="Bullet list"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
        >
          <List size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          testid={`${testid}-ol`}
          title="Ordered list"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
        >
          <ListOrdered size={15} />
        </ToolbarBtn>

        <span className="mx-1 w-px h-5 bg-gray-300" />

        <ToolbarBtn
          testid={`${testid}-link`}
          title="Insert link"
          onClick={openLinkPrompt}
          active={editor.isActive('link')}
        >
          <LinkIcon size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          testid={`${testid}-unlink`}
          title="Remove link"
          onClick={() => editor.chain().focus().unsetLink().run()}
          disabled={!editor.isActive('link')}
        >
          <Unlink size={15} />
        </ToolbarBtn>

        <span className="mx-1 w-px h-5 bg-gray-300" />

        <ToolbarBtn
          testid={`${testid}-undo`}
          title="Undo"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().undo()}
        >
          <Undo2 size={15} />
        </ToolbarBtn>
        <ToolbarBtn
          testid={`${testid}-redo`}
          title="Redo"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().redo()}
        >
          <Redo2 size={15} />
        </ToolbarBtn>
      </div>

      {showLink && (
        <div className="flex items-center gap-2 px-3 py-2 border-b bg-blue-50">
          <input
            type="text"
            autoFocus
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applyLink()}
            placeholder="https://example.com"
            className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#0a3d8f]/30"
            data-testid={`${testid}-link-input`}
          />
          <button
            type="button"
            onClick={applyLink}
            data-testid={`${testid}-link-apply`}
            className="text-xs font-semibold bg-[#0a3d8f] text-white px-3 py-1 rounded hover:bg-[#082e6b]"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => setShowLink(false)}
            className="text-xs font-semibold text-gray-600 hover:text-gray-900"
          >
            Batal
          </button>
        </div>
      )}

      <EditorContent editor={editor} />
    </div>
  );
};

export default RichTextField;
